import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  alpha,
  Button,
  TextField,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  AccountCircleRounded,
  EditRounded,
  KeyRounded,
  BadgeRounded,
  MailOutlineRounded,
  PhoneRounded,
  HistoryRounded,
  ShieldRounded,
} from '@mui/icons-material';
import api, { errorMessage } from '../lib/api';
import { formatDateTime, relativeTime } from '../lib/format';
import { palette } from '../theme';
import { GlassPanel, SectionTitle, StatusPill } from '../components/ui/GlassPanel';
import { useToast } from '../components/ui/ToastProvider';
import { useAuth } from '../App';

export default function Profile() {
  const toast = useToast();
  const { user, setUser } = useAuth();

  const [editOpen, setEditOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [profile, setProfile] = useState({ full_name: '', email: '', phone: '' });
  const [passwords, setPasswords] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setProfile({
        full_name: user.full_name || '',
        email: user.email || '',
        phone: user.phone || '',
      });
    }
  }, [user]);

  const saveProfile = async () => {
    setSaving(true);
    setErrors({});
    try {
      const response = await api.patch('/auth/me', profile);
      setUser(response.data);
      toast.success('Profile details updated.');
      setEditOpen(false);
    } catch (error) {
      toast.error(errorMessage(error, 'The profile could not be updated.'));
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async () => {
    const next = {};
    if (!passwords.current_password) next.current_password = 'Current passphrase is required';
    if (!passwords.new_password || passwords.new_password.length < 6) next.new_password = 'Minimum 6 characters';
    if (passwords.new_password !== passwords.confirm_password) next.confirm_password = 'Passphrases do not match';
    setErrors(next);
    if (Object.keys(next).length) return;

    setSaving(true);
    try {
      await api.post('/auth/change-password', {
        current_password: passwords.current_password,
        new_password: passwords.new_password,
      });
      toast.success('Passphrase rotated successfully.');
      setPasswords({ current_password: '', new_password: '', confirm_password: '' });
      setPasswordOpen(false);
    } catch (error) {
      toast.error(errorMessage(error, 'The passphrase could not be changed.'));
    } finally {
      setSaving(false);
    }
  };

  const fields = [
    { label: 'User ID', value: user?.username, icon: <BadgeRounded sx={{ fontSize: 15 }} /> },
    { label: 'Full Name', value: user?.full_name || '—', icon: <AccountCircleRounded sx={{ fontSize: 15 }} /> },
    { label: 'Email', value: user?.email, icon: <MailOutlineRounded sx={{ fontSize: 15 }} /> },
    { label: 'Phone', value: user?.phone || '—', icon: <PhoneRounded sx={{ fontSize: 15 }} /> },
    { label: 'Role', value: user?.role === 'admin' ? 'Administrator' : 'Operator', icon: <ShieldRounded sx={{ fontSize: 15 }} /> },
    { label: 'Last Login', value: user?.last_login ? formatDateTime(user.last_login) : '—', icon: <HistoryRounded sx={{ fontSize: 15 }} /> },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={4}>
          <GlassPanel sx={{ p: 3, height: '100%', textAlign: 'center' }}>
            <Box
              sx={{
                width: 88,
                height: 88,
                margin: '0 auto',
                borderRadius: 4,
                display: 'grid',
                placeItems: 'center',
                background: `linear-gradient(140deg, ${alpha(palette.primary, 0.85)}, ${alpha(palette.secondary, 0.8)})`,
                boxShadow: `0 22px 48px -18px ${alpha(palette.primary, 0.85)}`,
                fontSize: '1.6rem',
                fontWeight: 700,
                color: '#04070F',
              }}
            >
              {(user?.full_name || user?.username || '?')
                .split(' ')
                .map((part) => part[0])
                .join('')
                .slice(0, 2)
                .toUpperCase()}
            </Box>

            <Typography sx={{ mt: 2, fontSize: '1.1rem', fontWeight: 700 }}>
              {user?.full_name || user?.username}
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>{user?.email}</Typography>

            <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'center' }}>
              <StatusPill
                label={user?.role === 'admin' ? 'Administrator' : 'Operator'}
                tone={user?.role === 'admin' ? 'secondary' : 'info'}
              />
            </Box>

            <Divider sx={{ borderColor: alpha(palette.primary, 0.1), my: 2.5 }} />

            <Box sx={{ display: 'grid', gap: 1 }}>
              <Button variant="contained" startIcon={<EditRounded sx={{ fontSize: 16 }} />} onClick={() => setEditOpen(true)}>
                Edit Profile
              </Button>
              <Button variant="outlined" startIcon={<KeyRounded sx={{ fontSize: 16 }} />} onClick={() => setPasswordOpen(true)}>
                Change Password
              </Button>
            </Box>
          </GlassPanel>
        </Grid>

        <Grid item xs={12} lg={8}>
          <GlassPanel sx={{ p: 3, height: '100%' }}>
            <SectionTitle
              title="Account Identity"
              subtitle="Attributes provisioned for this operator account"
              icon={<AccountCircleRounded sx={{ fontSize: 18 }} />}
            />
            <Box sx={{ mt: 2.5, display: 'grid', gap: 1.75, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
              {fields.map((field) => (
                <Box
                  key={field.label}
                  sx={{
                    p: 1.75,
                    borderRadius: 3,
                    border: `1px solid ${alpha(palette.primary, 0.12)}`,
                    background: alpha(palette.primary, 0.045),
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.secondary', mb: 0.6 }}>
                    {field.icon}
                    <Typography sx={{ fontSize: '0.62rem', letterSpacing: '0.14em' }}>{field.label.toUpperCase()}</Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.88rem', fontWeight: 600 }}>{field.value}</Typography>
                </Box>
              ))}
            </Box>

            <Divider sx={{ borderColor: alpha(palette.primary, 0.1), my: 2.5 }} />

            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              <Box>
                <Typography sx={{ fontSize: '0.62rem', letterSpacing: '0.14em', color: 'text.secondary' }}>
                  MEMBER SINCE
                </Typography>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>{formatDateTime(user?.created_at)}</Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: '0.62rem', letterSpacing: '0.14em', color: 'text.secondary' }}>
                  LAST SESSION
                </Typography>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>
                  {relativeTime(user?.last_login)}
                </Typography>
              </Box>
            </Box>
          </GlassPanel>
        </Grid>
      </Grid>

      {/* edit profile */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Edit profile</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gap: 2, pt: 0.5 }}>
            <TextField
              fullWidth
              size="small"
              label="Full name"
              value={profile.full_name}
              onChange={(event) => setProfile({ ...profile, full_name: event.target.value })}
            />
            <TextField
              fullWidth
              size="small"
              label="Email"
              type="email"
              value={profile.email}
              onChange={(event) => setProfile({ ...profile, email: event.target.value })}
            />
            <TextField
              fullWidth
              size="small"
              label="Phone"
              value={profile.phone}
              onChange={(event) => setProfile({ ...profile, phone: event.target.value })}
            />
          </Box>
        </DialogContent>
        <Divider sx={{ borderColor: alpha(palette.primary, 0.1) }} />
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setEditOpen(false)} sx={{ color: 'text.secondary' }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={saveProfile} disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* change password */}
      <Dialog open={passwordOpen} onClose={() => setPasswordOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Change password</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gap: 2, pt: 0.5 }}>
            <TextField
              fullWidth
              size="small"
              type="password"
              label="Current passphrase"
              value={passwords.current_password}
              onChange={(event) => setPasswords({ ...passwords, current_password: event.target.value })}
              error={Boolean(errors.current_password)}
              helperText={errors.current_password}
            />
            <TextField
              fullWidth
              size="small"
              type="password"
              label="New passphrase"
              value={passwords.new_password}
              onChange={(event) => setPasswords({ ...passwords, new_password: event.target.value })}
              error={Boolean(errors.new_password)}
              helperText={errors.new_password || 'Minimum 6 characters'}
            />
            <TextField
              fullWidth
              size="small"
              type="password"
              label="Confirm new passphrase"
              value={passwords.confirm_password}
              onChange={(event) => setPasswords({ ...passwords, confirm_password: event.target.value })}
              error={Boolean(errors.confirm_password)}
              helperText={errors.confirm_password}
            />
          </Box>
        </DialogContent>
        <Divider sx={{ borderColor: alpha(palette.primary, 0.1) }} />
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setPasswordOpen(false)} sx={{ color: 'text.secondary' }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={changePassword} disabled={saving}>
            {saving ? 'Updating…' : 'Update passphrase'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
